import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { yieldCallback } from '@zillow/yield-callback';

import AppActions from 'app/shared/flux/actions/AppActions';
import ConversationActions from 'app/shared/flux/actions/ConversationActions';
import { areaUtils_getBestArea } from 'app/shared/utils/areaUtils';
import { filterUtils_getPropertyForRent } from 'app/shared/utils/filterUtils';
import HamburgerNav from 'app/shared/modules/navigation/header/HamburgerNav';
import ConversationBadge from 'app/shared/modules/ConversationBadge';
import HeaderNavMenuItem from 'app/shared/modules/navigation/header/HeaderNavMenuItem';
import HeaderNavMobileLogo from 'app/shared/modules/navigation/header/HeaderNavMobileLogo';
import AutocompleteSearchInput from 'app/shared/modules/location/AutocompleteSearchInput';
import hotpadsLogoWithText from 'images/hotpads-logo-with-text.min.svg';
import Linker from 'app/shared/modules/Linker';
import LoginStatus from 'app/shared/modules/LoginStatus';
import pathsManifest from 'app/shared/utils/pathsManifest';
import SkipToMain from 'app/shared/modules/SkipToMain';
import VisuallyHidden from 'app/shared/modules/VisuallyHidden';
import routeUtils from 'app/shared/utils/routeUtils';
import { zindex } from 'app/shared/styles/_zindex';
import { colors } from 'app/shared/styles/_colors';
import { headerHeights } from 'app/shared/styles/_variables';
import { viewports } from 'app/shared/styles/_breakpoints';
import { isFeatureFlagEnabled } from 'app/shared/utils/featureFlagUtils';
import ExperimentConstants from 'app/shared/constants/ExperimentConstants';
import HeaderMockInquiriesWarning from './HeaderMockInquiryWarning';
import {
  autocompleteContainerCX,
  headerNavMobileContainerCX,
  headerNavMobileLogoCX,
} from 'app/shared/modules/navigation/header/HeaderNavMobileComponent.styles';
import {
  headerNavMdAndUpCX,
  headerNavMdAndUpHeaderNavCX,
  headerNavMdAndUpLogoCX,
  headerNavMdAndUpLogoWrapperCX,
  headerNavMdAndUpMenuIconCX,
  headerNavMdAndUpMenuIconSpanCX,
  headerNavMdAndUpNavListCX,
} from 'app/shared/modules/navigation/header/HeaderNavMdAndUp.styles';
import type { RootReduxState } from 'app/shared/flux/reducers';

const StyledHeaderNav = styled.div<{ $hideOnMobile?: boolean }>`
  height: ${(props) => (props.$hideOnMobile ? '0' : headerHeights['mobile-header-height'])};
  @media ${viewports['md-and-up']} {
    height: ${headerHeights['desktop-header-height']};
  }
`;

const StyledHeaderNavWrapper = styled.div`
  left: 0;
  background: ${colors['$hpx-white']};
  z-index: ${zindex['$z-index-loader']};
  right: 0;
  top: 0;
  position: fixed;
`;

const HeaderNav: React.FC = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const [mockInquiriesWarning, setMockInquiriesWarning] = useState(false);
  const savedActiveElement = React.useRef<HTMLElement | null>(null);

  // Recent search sync is handled by SearchResultsPage.syncRecentSearchIfStale, not here.
  const area = useSelector((state: RootReduxState) => areaUtils_getBestArea(state));
  const hamburgerState = useSelector((state: RootReduxState) => state.app.hamburger);
  const searchSlug = useSelector((state: RootReduxState) => state.filter.search.slug);
  const unreadMsgCount = useSelector((state: RootReduxState) => state.user.unreadMsgCount);
  const runningExperiments = useSelector((state: RootReduxState) => state.analytics.runningExperiments);

  const pathname = location.pathname;
  const isPadOrBuildingUrl = routeUtils.isPadOrBuildingUrl(pathname);
  const isAreaPage = routeUtils.isAreaUrl(pathname);
  const isAreaUrlOrNearMe = isAreaPage || routeUtils.isNearMeUrl(pathname);
  const isFrontPage = pathname === '/';

  const fetchMessageCount = useCallback(() => {
    dispatch(ConversationActions.fetchMessageCount());
  }, [dispatch]);

  useEffect(() => {
    fetchMessageCount();
  }, [fetchMessageCount, pathname]);

  useEffect(() => {
    if (isFeatureFlagEnabled('mockInquiries')) {
      setMockInquiriesWarning(true);
    }
  }, []);

  const handleOpenHamburgerNav = yieldCallback(() => {
    savedActiveElement.current = document.activeElement as HTMLElement;
    dispatch(AppActions.activateHamburger());
  });

  const handleCloseHamburgerNav = () => {
    if (savedActiveElement.current) {
      savedActiveElement.current.focus();
    }
    dispatch(AppActions.deactivateHamburger());
    savedActiveElement.current = null;
  };

  const { apartmentsForRent, housesForRent } = filterUtils_getPropertyForRent(area);

  // HPWEB-6587: Hide Houses link if user is enrolled in apartment-only experiment
  const isApartmentOnlyExperiment =
    runningExperiments?.[ExperimentConstants.HOTPADS_WEB_APARTMENT_ONLY_LINKS]?.isControl === false;

  return (
    <>
      <StyledHeaderNav $hideOnMobile={isPadOrBuildingUrl}>
        <SkipToMain />
        <StyledHeaderNavWrapper>
          {mockInquiriesWarning && <HeaderMockInquiriesWarning />}

          {/* Mobile header - hidden on md+ via PandaCSS */}
          {!isPadOrBuildingUrl && (
            <nav className={headerNavMobileContainerCX} data-name="HeaderNavMobileContainer">
              <HeaderNavMobileLogo
                className={headerNavMobileLogoCX}
                logoSize={isFrontPage ? 'full' : null}
                onCloseHamburgerNav={handleCloseHamburgerNav}
                onClick={handleOpenHamburgerNav}
                unreadMsgCount={unreadMsgCount}
              />
              {isAreaUrlOrNearMe ? (
                <div className={autocompleteContainerCX} data-name="AutocompleteContainer">
                  <AutocompleteSearchInput
                    includeCancel
                    includeClear
                    full
                    showRecentSearchItem={false}
                    useSmallScreenWidthDropdown
                  />
                </div>
              ) : (
                <LoginStatus />
              )}
            </nav>
          )}

          {/* Desktop header - hidden on mobile via PandaCSS */}
          <div className={headerNavMdAndUpCX}>
            <header>
              <HamburgerNav onCloseHamburgerNav={handleCloseHamburgerNav} />
              <nav className={headerNavMdAndUpHeaderNavCX} aria-label="main">
                <button
                  className={headerNavMdAndUpMenuIconCX}
                  onClick={handleOpenHamburgerNav}
                  aria-label="HotPads navigation menu"
                  aria-expanded={hamburgerState}
                >
                  <VisuallyHidden>Navigation menu</VisuallyHidden>
                  <span className={headerNavMdAndUpMenuIconSpanCX} />
                </button>
                <div className={headerNavMdAndUpLogoWrapperCX}>
                  <Linker to={pathsManifest.FrontPage}>
                    <img
                      src={hotpadsLogoWithText}
                      className={headerNavMdAndUpLogoCX}
                      alt="HotPads"
                      fetchpriority="low"
                      decoding="async"
                    />
                  </Linker>
                </div>
                <ul className={headerNavMdAndUpNavListCX}>
                  <HeaderNavMenuItem
                    active={isAreaPage && searchSlug === 'apartments-for-rent'}
                    mainLinkUrl={apartmentsForRent}
                  >
                    Apartments
                  </HeaderNavMenuItem>
                  {!isApartmentOnlyExperiment && (
                    <HeaderNavMenuItem
                      active={isAreaPage && searchSlug === 'houses-for-rent'}
                      mainLinkUrl={housesForRent}
                    >
                      Houses
                    </HeaderNavMenuItem>
                  )}
                  <HeaderNavMenuItem
                    active={pathname === '/user/listings/my-hub'}
                    mainLinkUrl={pathsManifest.MyHubPage}
                  >
                    My Hub
                  </HeaderNavMenuItem>
                  <HeaderNavMenuItem mainLinkUrl={pathsManifest.InboxPage} escape>
                    Messages
                    {unreadMsgCount > 0 && <ConversationBadge>{unreadMsgCount}</ConversationBadge>}
                  </HeaderNavMenuItem>
                  <HeaderNavMenuItem
                    active={pathname === '/user/saved-search'}
                    mainLinkUrl={pathsManifest.SavedSearchPage}
                  >
                    Saved searches
                  </HeaderNavMenuItem>
                </ul>
                <LoginStatus />
              </nav>
            </header>
          </div>
        </StyledHeaderNavWrapper>
      </StyledHeaderNav>
      <div id="skip-topnav-target" tabIndex={-1} />
    </>
  );
};

export default HeaderNav;
